import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiChevronDown, FiX } from 'react-icons/fi'
import SingleDateCalendar from '@/components/ui/SingleDateCalendar'
import AddSupplierModal from './AddSupplierModal'
import SkuInput from '@/components/contracts/SkuInput'
import PaymentTermsInput from '@/components/contracts/PaymentTermsInput'
import ContractPriceInput from '@/components/contracts/ContractPriceInput'
import ProductSelectInput from '@/components/products/ProductSelectInput'
import CustomerSelectInput from '@/components/customers/CustomerSelectInput'
import ShippingPeriodInput from '@/components/contracts/ShippingPeriodInput'
import { DEFAULT_CONTRACT_CURRENCY } from '@/constants/contractCurrencies'
import { createContract, uploadContractFile } from '@/services/api/contracts'
import { normalizeSkuForSave } from '@/utils/skuFormat'
import { formatShippingPeriodForSave, toISODateString } from '@/utils/shippingPeriod'
import { getVendors } from '@/services/api/vendors'
import type { Vendor } from '@/services/api/vendors'
import { vendorsForContractPicker } from '@/utils/vendorFilters'

interface UploadContractFileModalProps {
  isOpen: boolean
  onClose: () => void
  initialContractType?: 'Sale Contract' | 'Buy Contract'
  onSuccess?: () => void
}

const UploadContractFileModal = ({ isOpen, onClose, initialContractType = 'Sale Contract', onSuccess }: UploadContractFileModalProps) => {
  const [formData, setFormData] = useState({
    contractType: initialContractType,
    contractNo: '',
    name: '',
    documentType: '',
    customer: '',
    contractTypeDropdown: '',
    seller: '',
    buyer: '',
    vendorId: '' as string,
    product: '',
    price: '',
    priceCurrency: DEFAULT_CONTRACT_CURRENCY,
    sku: '',
    qty: '',
    incoterm: '',
    deliveryConditions: '',
    paymentTerms: '',
    pol: '',
    pod: '',
    notes: '',
    dateModified: '',
    buyContractFile: null as File | null
  })
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const isContractNumberValid = (value: string) => /[A-Za-z0-9]/.test(value)

  const [showContractTypeSuggestions, setShowContractTypeSuggestions] = useState(false)
  const [showIncotermSuggestions, setShowIncotermSuggestions] = useState(false)
  const [shippingStartDate, setShippingStartDate] = useState<Date | null>(null)
  const [shippingEndDate, setShippingEndDate] = useState<Date | null>(null)
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [contractTypeSearchQuery, setContractTypeSearchQuery] = useState('')
  const [_sellerSearchQuery, setSellerSearchQuery] = useState('')
  const [incotermSearchQuery, setIncotermSearchQuery] = useState('')
  const dateInputRef = useRef<HTMLInputElement>(null)

  // Suggestions - contract type dropdown options
  const contractTypeSuggestions: string[] = ['Buy Contract', 'Sale Contract']
  const incotermSuggestions: string[] = []

  // Filter suggestions based on search query
  const filteredContractTypeSuggestions = contractTypeSearchQuery.length > 0
    ? contractTypeSuggestions.filter(type =>
        type.toLowerCase().includes(contractTypeSearchQuery.toLowerCase())
      )
    : contractTypeSuggestions

  const filteredIncotermSuggestions = incotermSearchQuery.length > 0
    ? incotermSuggestions.filter(incoterm =>
        incoterm.toLowerCase().includes(incotermSearchQuery.toLowerCase())
      )
    : incotermSuggestions

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleContractTypeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setContractTypeSearchQuery(value)
    setFormData({
      ...formData,
      contractTypeDropdown: value
    })
    setShowContractTypeSuggestions(true)
  }

  const handleContractTypeSelect = (type: string) => {
    const isBuy = type === 'Buy Contract'
    setFormData({
      ...formData,
      contractTypeDropdown: type,
      contractType: isBuy ? 'Buy Contract' : 'Sale Contract',
      vendorId: '',
      seller: '',
    })
    setContractTypeSearchQuery(type)
    setShowContractTypeSuggestions(false)
  }

  const handleIncotermInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setIncotermSearchQuery(value)
    setFormData({
      ...formData,
      incoterm: value
    })
    setShowIncotermSuggestions(true)
  }

  const handleIncotermSelect = (incoterm: string) => {
    setFormData({
      ...formData,
      incoterm: incoterm
    })
    setIncotermSearchQuery(incoterm)
    setShowIncotermSuggestions(false)
  }

  // Use the shared date formatting utility
  const formatDate = (date: Date | null): string => {
    if (!date) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}` // DD/MM/YYYY format to match Figma
  }

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date)
    setFormData((prev) => ({
      ...prev,
      dateModified: date ? formatDate(date) : '',
    }))
    setShowCalendar(false)
  }

  const handleSellerAdded = (vendor: Vendor) => {
    setFormData({
      ...formData,
      vendorId: vendor.id,
      seller: vendor.name,
      customer: formData.contractType === 'Buy Contract' ? vendor.name : formData.customer,
    })
    setSellerSearchQuery(vendor.name)
    getVendors({ limit: 200 })
      .then((res) => setVendors(vendorsForContractPicker(res.vendors)))
      .catch(() => {})
  }

  const handleContractTypeChange = (type: 'Sale Contract' | 'Buy Contract') => {
    setFormData({
      ...formData,
      contractType: type,
      vendorId: '',
      seller: '',
    })
  }

  // Fetch vendors and sync contract type dropdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        contractType: initialContractType,
        contractTypeDropdown: initialContractType,
      }))
      setContractTypeSearchQuery(initialContractType)
      setShippingStartDate(null)
      setShippingEndDate(null)
      getVendors({ limit: 200 })
        .then((res) => setVendors(vendorsForContractPicker(res.vendors)))
        .catch(() => setVendors([]))
    }
  }, [isOpen, initialContractType])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    if (!formData.vendorId) {
      setSubmitError(formData.contractType === 'Buy Contract' ? 'Please select a supplier.' : 'Please select a seller.')
      return
    }
    if (!formData.contractNo?.trim()) {
      setSubmitError('Contract number is required.')
      return
    }
    if (!isContractNumberValid(formData.contractNo.trim())) {
      setSubmitError('Contract number must include at least one letter or number.')
      return
    }
    setSubmitting(true)
    try {
      const contract = await createContract({
        vendorId: formData.vendorId,
        contractNumber: formData.contractNo.trim(),
        title: formData.contractNo.trim(),
        type: formData.contractType === 'Buy Contract' ? 'buy' : 'sale',
        productSpecifications: formData.product || undefined,
        sku: normalizeSkuForSave(formData.sku),
        quantity: formData.qty?.trim() || undefined,
        price: formData.price?.trim() || undefined,
        priceCurrency: formData.priceCurrency || DEFAULT_CONTRACT_CURRENCY,
        deliveryConditions: formData.deliveryConditions?.trim() || undefined,
        startDate: toISODateString(shippingStartDate),
        endDate: toISODateString(shippingEndDate),
        deliveryPeriod: formatShippingPeriodForSave(shippingStartDate, shippingEndDate),
        incoterm: formData.incoterm?.trim() || undefined,
        customer: formData.buyer?.trim() || undefined,
        pol: formData.pol?.trim() || undefined,
        pod: formData.pod?.trim() || undefined,
        paymentTerms: formData.paymentTerms?.trim() || undefined,
      })
      if (formData.buyContractFile) {
        await uploadContractFile(contract.id, formData.buyContractFile)
      }
      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create contract')
    } finally {
      setSubmitting(false)
    }
  }

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY
      // Disable body scroll
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        // Re-enable body scroll when modal closes
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      style={{ zIndex: 60, overflow: 'hidden' }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '600px',
          maxWidth: '600px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <style>{`
          #upload-contract-form input::placeholder,
          #upload-contract-form select option[value=""] {
            color: #69697A !important;
            font-family: 'Lexend Deca';
            font-weight: 300;
            font-style: normal;
            font-size: 12px;
            line-height: 20px;
            letter-spacing: 0px;
          }
          #upload-contract-form input:not(:placeholder-shown),
          #upload-contract-form select:not([value=""]) {
            color: #212126 !important;
            font-family: 'Lexend Deca';
            font-weight: 500;
            font-style: normal;
            font-size: 12px;
            line-height: 20px;
          }
        `}</style>
        
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            Upload Contract File
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px',
              cursor: 'pointer'
            }}
          >
            <FiX style={{ width: '24px', height: '24px', color: '#212126' }} />
          </button>
        </div>

        {/* Form Content */}
        <form 
          id="upload-contract-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          {submitError && (
            <div style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#E93323' }}>{submitError}</div>
          )}
          {/* Contract Type Radio Buttons */}
          <div>
            <label 
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '22px',
                letterSpacing: '0px',
                color: '#69697A',
                display: 'block',
                marginBottom: '12px'
              }}
            >
              Contract Type
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="contractType"
                  value="Sale Contract"
                  checked={formData.contractType === 'Sale Contract'}
                  onChange={() => handleContractTypeChange('Sale Contract')}
                  style={{
                    width: '16px',
                    height: '16px',
                    accentColor: '#212126',
                    cursor: 'pointer'
                  }}
                />
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#212126'
                  }}
                >
                  Sale Contract
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="contractType"
                  value="Buy Contract"
                  checked={formData.contractType === 'Buy Contract'}
                  onChange={() => handleContractTypeChange('Buy Contract')}
                  style={{
                    width: '16px',
                    height: '16px',
                    accentColor: '#212126',
                    cursor: 'pointer'
                  }}
                />
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#212126'
                  }}
                >
                  Buy Contract
                </span>
              </label>
            </div>
          </div>

          {/* Form Fields - Two Column Layout matching table columns */}
          <div className="flex flex-col gap-4">
            {/* Row 1: Contract No. | Contract Type (dropdown) - common */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Contract No.
                </label>
                <input
                  type="text"
                  name="contractNo"
                  value={formData.contractNo}
                  onChange={handleChange}
                  placeholder="Enter contract no."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                />
              </div>
              <div className="relative">
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                  Contract Type
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="contractTypeDropdown"
                    value={formData.contractTypeDropdown}
                    onChange={handleContractTypeInputChange}
                    onFocus={() => { if (filteredContractTypeSuggestions.length > 0) setShowContractTypeSuggestions(true) }}
                    onBlur={() => { setTimeout(() => setShowContractTypeSuggestions(false), 200) }}
                    placeholder="Select contract type"
                    className="w-full px-4 py-2 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px', color: formData.contractTypeDropdown ? '#212126' : '#69697A', fontWeight: formData.contractTypeDropdown ? 500 : 300 }}
                  />
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#212126' }} />
                </div>
                {showContractTypeSuggestions && filteredContractTypeSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0px', backgroundColor: '#FFFFFF', border: '1px solid #E9E9EA', borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                    {filteredContractTypeSuggestions.map((suggestion, index) => (
                      <div key={index} onClick={() => handleContractTypeSelect(suggestion)} style={{ padding: '12px 16px', cursor: 'pointer', fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#212126', borderBottom: index < filteredContractTypeSuggestions.length - 1 ? '1px solid #E9E9EA' : 'none', backgroundColor: formData.contractTypeDropdown === suggestion ? '#F3F4F6' : 'transparent' }} className="hover:bg-gray-50 transition-colors">
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Supplier (Buy) OR Supplier | Customer (Sale) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block' }}>
                    Supplier *
                  </label>
                  <button type="button" onClick={(e) => { e.preventDefault(); setShowAddSupplierModal(true) }} style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#212126', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                    Add Supplier
                  </button>
                </div>
                <select
                  value={formData.vendorId}
                  onChange={(e) => {
                    const v = vendors.find((x) => x.id === e.target.value)
                    setFormData({ ...formData, vendorId: e.target.value, seller: v?.name ?? '' })
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }}
                >
                  <option value="">Select supplier</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              {formData.contractType === 'Sale Contract' && (
                <div className="relative">
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>
                    Customer
                  </label>
                  <CustomerSelectInput
                    value={formData.buyer}
                    onChange={(buyer) => setFormData((prev) => ({ ...prev, buyer }))}
                  />
                </div>
              )}
            </div>

            {/* Product | SKU (Buy and Sale) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Product</label>
                <ProductSelectInput
                  value={formData.product}
                  onChange={(product) => setFormData((prev) => ({ ...prev, product }))}
                />
              </div>
              <div>
                <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>SKU (kg)</label>
                <SkuInput
                  value={formData.sku}
                  onChange={(sku) => setFormData((prev) => ({ ...prev, sku }))}
                />
              </div>
            </div>

            {formData.contractType === 'Buy Contract' ? (
              <>
                {/* Buy: Qty */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Qty</label>
                    <input type="text" name="qty" value={formData.qty} onChange={handleChange} placeholder="Enter qty" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                  </div>
                </div>
                <ContractPriceInput
                  price={formData.price}
                  currency={formData.priceCurrency}
                  onPriceChange={(price) => setFormData((prev) => ({ ...prev, price }))}
                  onCurrencyChange={(priceCurrency) => setFormData((prev) => ({ ...prev, priceCurrency }))}
                />
              </>
            ) : (
              <>
                {/* Sale: Qty */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Qty</label>
                    <input type="text" name="qty" value={formData.qty} onChange={handleChange} placeholder="Enter qty" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                  </div>
                </div>
                <ContractPriceInput
                  price={formData.price}
                  currency={formData.priceCurrency}
                  onPriceChange={(price) => setFormData((prev) => ({ ...prev, price }))}
                  onCurrencyChange={(priceCurrency) => setFormData((prev) => ({ ...prev, priceCurrency }))}
                />
                {/* Sale: Incoterm */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Incoterm</label>
                    <div className="relative">
                      <input type="text" name="incoterm" value={formData.incoterm} onChange={handleIncotermInputChange} onFocus={() => { if (filteredIncotermSuggestions.length > 0) setShowIncotermSuggestions(true) }} onBlur={() => { setTimeout(() => setShowIncotermSuggestions(false), 200) }} placeholder="Select incoterm" className="w-full px-4 py-2 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                      <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#212126' }} />
                    </div>
                    {showIncotermSuggestions && filteredIncotermSuggestions.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0px', backgroundColor: '#FFFFFF', border: '1px solid #E9E9EA', borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                        {filteredIncotermSuggestions.map((suggestion, index) => (
                          <div key={index} onClick={() => handleIncotermSelect(suggestion)} style={{ padding: '12px 16px', cursor: 'pointer', fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#212126', borderBottom: index < filteredIncotermSuggestions.length - 1 ? '1px solid #E9E9EA' : 'none' }} className="hover:bg-gray-50 transition-colors">
                            {suggestion}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Incoterm | POL (Buy) OR POL | POD (Sale) */}
            {formData.contractType === 'Buy Contract' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Incoterm</label>
                  <div className="relative">
                    <input type="text" name="incoterm" value={formData.incoterm} onChange={handleIncotermInputChange} onFocus={() => { if (filteredIncotermSuggestions.length > 0) setShowIncotermSuggestions(true) }} onBlur={() => { setTimeout(() => setShowIncotermSuggestions(false), 200) }} placeholder="Select incoterm" className="w-full px-4 py-2 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                    <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#212126' }} />
                  </div>
                  {showIncotermSuggestions && filteredIncotermSuggestions.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0px', backgroundColor: '#FFFFFF', border: '1px solid #E9E9EA', borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                      {filteredIncotermSuggestions.map((suggestion, index) => (
                        <div key={index} onClick={() => handleIncotermSelect(suggestion)} style={{ padding: '12px 16px', cursor: 'pointer', fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#212126', borderBottom: index < filteredIncotermSuggestions.length - 1 ? '1px solid #E9E9EA' : 'none' }} className="hover:bg-gray-50 transition-colors">
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>POL</label>
                  <input type="text" name="pol" value={formData.pol} onChange={handleChange} placeholder="Enter port of loading" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>POL</label>
                  <input type="text" name="pol" value={formData.pol} onChange={handleChange} placeholder="Enter port of loading" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>POD</label>
                  <input type="text" name="pod" value={formData.pod} onChange={handleChange} placeholder="Enter port of discharge" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                </div>
              </div>
            )}

            {/* POD | Delivery Conditions (Buy) OR Delivery Conditions | Shipping Period (Sale) */}
            {formData.contractType === 'Buy Contract' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>POD</label>
                  <input type="text" name="pod" value={formData.pod} onChange={handleChange} placeholder="Enter port of discharge" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Delivery Conditions</label>
                  <input type="text" name="deliveryConditions" value={formData.deliveryConditions} onChange={handleChange} placeholder="Enter delivery conditions" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Delivery Conditions</label>
                  <input type="text" name="deliveryConditions" value={formData.deliveryConditions} onChange={handleChange} placeholder="Enter delivery conditions" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} />
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Shipping Period</label>
                  <ShippingPeriodInput
                    startDate={shippingStartDate}
                    endDate={shippingEndDate}
                    onChange={(start, end) => {
                      setShippingStartDate(start)
                      setShippingEndDate(end)
                    }}
                  />
                </div>
              </div>
            )}

            {/* Shipping Period | Payment Terms (Buy) OR Payment Terms | Contract File (Sale) */}
            {formData.contractType === 'Buy Contract' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Shipping Period</label>
                  <ShippingPeriodInput
                    startDate={shippingStartDate}
                    endDate={shippingEndDate}
                    onChange={(start, end) => {
                      setShippingStartDate(start)
                      setShippingEndDate(end)
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Payment Terms</label>
                  <PaymentTermsInput
                    value={formData.paymentTerms}
                    onChange={(paymentTerms) => setFormData((prev) => ({ ...prev, paymentTerms }))}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Payment Terms</label>
                  <PaymentTermsInput
                    value={formData.paymentTerms}
                    onChange={(paymentTerms) => setFormData((prev) => ({ ...prev, paymentTerms }))}
                  />
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Sale Contract</label>
                  <div>
                    <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) setFormData({ ...formData, buyContractFile: e.target.files[0] }) }} className="hidden" id="sale-contract-file-input" />
                    <button type="button" onClick={() => document.getElementById('sale-contract-file-input')?.click()} className="rounded-lg hover:opacity-90 transition-opacity" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#212126', backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E9E9EA', width: '100%', height: '40px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                      {formData.buyContractFile ? formData.buyContractFile.name : 'Upload'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Contract File | Date Modified (Buy) OR Date Modified (Sale) */}
            {formData.contractType === 'Buy Contract' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Buy Contract</label>
                  <div>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => { if (e.target.files && e.target.files[0]) setFormData({ ...formData, buyContractFile: e.target.files[0] }) }} className="hidden" id="buy-contract-file-input" />
                    <button type="button" onClick={() => document.getElementById('buy-contract-file-input')?.click()} className="rounded-lg hover:opacity-90 transition-opacity" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#212126', backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E9E9EA', width: '100%', height: '40px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                      {formData.buyContractFile ? formData.buyContractFile.name : 'Upload'}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Date Modified</label>
                  <div className="relative" style={{ position: 'relative', zIndex: showCalendar ? 100 : 1 }}>
                    <input ref={dateInputRef} type="text" name="dateModified" value={formData.dateModified} onChange={handleChange} onFocus={() => setShowCalendar(true)} placeholder="DD/MM/YYYY" className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} readOnly />
                    <img src="/images/calendar.svg" alt="Calendar" onClick={() => setShowCalendar(true)} role="button" className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer" style={{ width: '18px', height: '18px', opacity: 0.6 }} />
                    {showCalendar && (
                      <SingleDateCalendar isOpen={showCalendar} onClose={() => setShowCalendar(false)} onDateSelect={handleDateSelect} selectedDate={selectedDate} inputRef={dateInputRef} />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', display: 'block', marginBottom: '8px' }}>Date Modified</label>
                  <div className="relative" style={{ position: 'relative', zIndex: showCalendar ? 100 : 1 }}>
                    <input ref={dateInputRef} type="text" name="dateModified" value={formData.dateModified} onChange={handleChange} onFocus={() => setShowCalendar(true)} placeholder="DD/MM/YYYY" className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', height: '40px', border: '1px solid #E9E9EA', borderRadius: '8px' }} readOnly />
                    <img src="/images/calendar.svg" alt="Calendar" onClick={() => setShowCalendar(true)} role="button" className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer" style={{ width: '18px', height: '18px', opacity: 0.6 }} />
                    {showCalendar && (
                      <SingleDateCalendar isOpen={showCalendar} onClose={() => setShowCalendar(false)} onDateSelect={handleDateSelect} selectedDate={selectedDate} inputRef={dateInputRef} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

        </form>

        {/* Bottom Action Bar */}
        <div 
          className="flex items-center justify-end gap-4 flex-shrink-0 border-t border-gray-200"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderBottomLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E9E9EA',
              borderRadius: '8px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="upload-contract-form"
            disabled={submitting}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontStyle: 'Medium',
              fontSize: '14px',
              lineHeight: '22px',
              letterSpacing: '0px',
              textAlign: 'center',
              color: '#FFFFFF',
              backgroundColor: '#212126',
              borderRadius: '8px'
            }}
          >
            {submitting ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>

      {/* Add Supplier Modal */}
      <AddSupplierModal
        isOpen={showAddSupplierModal}
        onClose={() => setShowAddSupplierModal(false)}
        onSupplierAdded={handleSellerAdded}
      />
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default UploadContractFileModal
